import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { lastValueFrom, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { LocalizationService } from '../../../localization/localization.service';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Store } from '@ngrx/store';
import {
  calculationInitialState,
  CalculationModelsState,
  Calculations,
  CalculationState,
  selectCalculations,
} from '../../calculation.state';
import { AppState } from '../../../app.state';
import { setAccessories, setCalculateAccessories, setCart, setRainSystem } from '../../calculation.actions';
import { cloneDeep, isNil, isNumber } from 'lodash-es';
import { ProgressSpinnerService } from '../../../components/progress-spinner/progress-spinner.service';
import { ProductTypeMap } from './accessories.constants';
import { CustomMeasurementDto } from '../../../../api/models/custom-measurement-dto';
import { CalculationsService } from '../../../../api/services/calculations.service';
import { OneSlopeAccessoriesMeasurementsDto } from '../../../../api/models/one-slope-accessories-measurements-dto';
import { AccessoryDto } from '../../../../api/models/accessory-dto';
import { TwoSlopeAccessoriesMeasurementsDto } from '../../../../api/models/two-slope-accessories-measurements-dto';
import { FourSlopeAccessoriesMeasurementsDto } from '../../../../api/models/four-slope-accessories-measurements-dto';
import { CustomShapesAccessoriesDto } from 'src/api/models/custom-shapes-accessories-dto';
import { CustomAccessoriesDto } from '../../../../api/models/custom-accessories-dto';
import { AccessoryCategoryEnum } from '../../../../api/models/accessory-category-enum';
import { CartService } from '../../../../api/services/cart.service';
import { RoofCalculationsService } from '../../../common/shared/roof-calculations.service';
import { ShoppingCartDto } from '../../../../api/models/shopping-cart-dto';
import { AccessoriesService } from '../../../../api/services/accessories.service';
import { AccessoryListItemDto } from '../../../../api/models/accessory-list-item-dto';
import { SessionService } from '../../../auth/session.service';
import { CoreComponent } from '../../../core.component';
import { RainSystemCustomizationsDto } from '../../../common/shared/rain-system-customizations/rain-system-customizations.dto';
import { ProductsMetaService } from '../../../cart/products-meta.service';
import {
  CalculationTypeEnum,
  FenceSlatAccessoriesMeasurementsDto,
  FenceSlatMeasurementsDto,
} from '../../../../api/models';

@Component({
  selector: 'roof-accessories',
  templateUrl: './accessories.component.html',
  styleUrls: ['./accessories.component.scss'],
})
export class AccessoriesComponent extends CoreComponent implements OnInit {
  accessories: AccessoryDto[] = [];
  allAccessories: AccessoryListItemDto[] = [];
  autocompleteFormControl = new UntypedFormControl('');
  filteredOptions!: Observable<AccessoryListItemDto[]>;
  autocompleteValue: any;
  private calculationState: Calculations = calculationInitialState;
  activeCalculation!: CalculationState;
  displayedColumns = ['label', 'quantity', 'actions'];
  productTypeMap = ProductTypeMap;

  finishes = [];
  models!: CalculationModelsState;
  accessoriesFormGroup = this._formBuilder.group({});
  displayRainSystemForm = false;
  rainSystemCustomizations?: RainSystemCustomizationsDto;
  minimumAccessories: boolean | null = false;
  imageBaseUrl = ProductsMetaService.baseImageUrl;

  @ViewChild('accessoriesInput') accessoriesInput!: ElementRef;
  @ViewChild(MatAutocompleteTrigger) accessoriesAutoComplete!: MatAutocompleteTrigger;
  private cart: ShoppingCartDto | undefined;

  constructor(
    private readonly store: Store<AppState>,
    private readonly accessoriesService: AccessoriesService,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly localizationService: LocalizationService,
    private readonly progressSpinnerService: ProgressSpinnerService,
    private readonly calculationsService: CalculationsService,
    private readonly cartService: CartService,
    private readonly sessionService: SessionService,
    private readonly roofCalculationsService: RoofCalculationsService,
  ) {
    super();
  }

  isManualCalculation(): boolean {
    return [CalculationTypeEnum.Custom, CalculationTypeEnum.CustomShape].includes(this.calculationState.activeTab);
  }
  isFenceCalculation(): boolean {
    return [CalculationTypeEnum.FenceSlat, CalculationTypeEnum.FencePanel].includes(this.calculationState.activeTab);
  }

  recalculateAccessories() {
    const activeTab = this.calculationState.activeTab;
    this.calculateAccessories(
      this.calculationState[activeTab],
      this.calculationState.models,
      this.minimumAccessories as boolean,
    );
  }

  async ngOnInit(): Promise<void> {
    this.garbageCollector.push(
      this.store.pipe(map((state) => selectCalculations(state))).subscribe(async (calculation) => {
        this.calculationState = calculation;
        const activeTab = calculation.activeTab;
        this.activeCalculation = calculation[activeTab];
        if (calculation.models?.producer && calculation.models.producer !== this.models?.producer) {
          this.allAccessories = await lastValueFrom(
            this.accessoriesService.accessoriesControllerGetAllAccessories({ producer: calculation.models?.producer }),
          );

          this.filteredOptions = this.autocompleteFormControl.valueChanges.pipe(
            startWith(''),
            map((value: AccessoryListItemDto | string) => (typeof value === 'string' ? value : value.name)),
            map((value) => this._filter(value)),
          );
        }
        if (this.models?.color && !calculation.models?.color) {
          console.error(`Models was reset ${JSON.stringify(calculation.models)}`);
        } else {
          this.models = calculation.models as CalculationModelsState;
        }
        this.cart = calculation.cart;
        if (this.activeCalculation.calculateAccessories) {
          if (isNil(this.minimumAccessories)) {
            this.minimumAccessories = false;
          }
          await this.calculateAccessories(this.activeCalculation, calculation.models, this.minimumAccessories ?? false);
        } else if (!this.accessories?.length && this.activeCalculation?.accessories?.length > 0) {
          this.minimumAccessories = null;
          this.populateForms(this.activeCalculation.accessories);
        }
      }),
    );
  }

  updateRainSystemState() {
    this.displayRainSystemForm = this.accessories.some(
      (accessory) => accessory.category === AccessoryCategoryEnum.RainSystem,
    );
    if (this.displayRainSystemForm) {
      this.store.dispatch(
        setRainSystem({
          rainSystem: { ...this.activeCalculation.rainSystem, ...this.rainSystemCustomizations, rainSystem: true },
        }),
      );
    } else {
      this.store.dispatch(setRainSystem({ rainSystem: { ...this.activeCalculation.rainSystem, rainSystem: false } }));
    }
  }

  private async calculateAccessories(
    { measurements, rainSystem, annexes, roofType }: CalculationState,
    { roof, finish, thickness, color }: any,
    minimumAccessories = false,
  ) {
    if (!finish || !thickness || !roof || !color || !measurements) {
      return;
    }
    this.progressSpinnerService.openProgressSpinner();
    try {
      this.store.dispatch(setCalculateAccessories({ calculateAccessories: false }));
      const body = {
        colorId: color.id,
        measurements: { ...measurements, rainSystem: rainSystem ?? { rainSystem: false } },
        annexes,
        minimumAccessories,
      };
      let accessories: AccessoryDto[] = [];
      switch (roofType) {
        case CalculationTypeEnum.OneSlope:
          accessories = await lastValueFrom(
            this.calculationsService.calculationsControllerOneSlopeAccessories({
              body: body as OneSlopeAccessoriesMeasurementsDto,
            }),
          );
          break;
        case CalculationTypeEnum.TwoSlopes:
          accessories = await lastValueFrom(
            this.calculationsService.calculationsControllerTwoSlopeAccessories({
              body: body as TwoSlopeAccessoriesMeasurementsDto,
            }),
          );
          break;
        case CalculationTypeEnum.FourSlopes:
          accessories = await lastValueFrom(
            this.calculationsService.calculationsControllerFourSlopeAccessories({
              body: body as FourSlopeAccessoriesMeasurementsDto,
            }),
          );
          break;
        case CalculationTypeEnum.CustomShape:
          accessories = await lastValueFrom(
            this.calculationsService.calculationsControllerCustomShapeAccessories({
              body: body as CustomShapesAccessoriesDto,
            }),
          );
          break;
        case CalculationTypeEnum.Custom:
          accessories = await lastValueFrom(
            this.calculationsService.calculationsControllerCustomAccessories({
              body: body as CustomAccessoriesDto,
            }),
          );
          break;
        case CalculationTypeEnum.FenceSlat:
          accessories = await lastValueFrom(
            this.calculationsService.calculationsControllerFenceSlatAccessories({
              body: {
                colorId: color.id,
                measurements: measurements as FenceSlatMeasurementsDto,
              } as FenceSlatAccessoriesMeasurementsDto,
            }),
          );
          break;
      }
      this.populateForms(accessories);
      this.store.dispatch(setAccessories({ accessories }));
    } catch (error) {
      console.error(error);
    }
    this.progressSpinnerService.closeProgressSpinner();
  }

  private _filter(value: string): AccessoryListItemDto[] {
    if (!value) {
      return this.allAccessories;
    }
    const filterValue = this.localizationService.removeDiacritics(value.toLowerCase());
    return this.allAccessories.filter(
      (option: AccessoryListItemDto) =>
        this.localizationService.removeDiacritics(option.name).toLowerCase().includes(filterValue) ||
        (option.alias && this.localizationService.removeDiacritics(option.alias).toLowerCase().includes(filterValue)),
    );
  }

  displayAccessoryName(accessory: any): string {
    return accessory?.name || '';
  }

  populateForms(accessories: any[]): void {
    this.accessories = cloneDeep(accessories);
    const accessoriesForm = this.accessories.reduce((obj: any, accessoryPrice: any) => {
      obj[accessoryPrice.id] = [accessoryPrice.quantity];
      return obj;
    }, {});
    this.accessoriesFormGroup = this._formBuilder.group(accessoriesForm);
    this.updateRainSystemState();
  }

  selectOption(e: MatAutocompleteSelectedEvent) {
    this.autocompleteValue = e.option.value;
  }

  addAccessory() {
    const accessoryExists = this.accessories.find((accessory) => accessory.id === this.autocompleteValue.id);
    if (accessoryExists) {
      alert('Accesoriul exista deja in lista');
      return;
    }
    const accessories = [
      ...this.accessories,
      { ...this.autocompleteValue, quantity: 1, label: this.autocompleteValue.name, name: undefined },
    ];
    this.populateForms(accessories);
    this.store.dispatch(setAccessories({ accessories }));
    this.updateRainSystemState();
    this.autocompleteFormControl.patchValue('');
    this.autocompleteValue = '';

    setTimeout(() => {
      this.accessoriesInput.nativeElement.blur();
      this.accessoriesAutoComplete.closePanel();
    }, 50);
  }

  removeAccessory(removedAccessory: any) {
    const accessories = this.accessories.filter((accessory) => accessory.id !== removedAccessory.id);
    this.populateForms(accessories);
    this.store.dispatch(setAccessories({ accessories }));
    this.updateRainSystemState();
  }

  changeAccessory(changedAccessory: AccessoryDto) {
    const index = this.accessories.findIndex(
      (accessory) => accessory.id === changedAccessory.id && accessory.category === changedAccessory.category,
    );
    const accessories = cloneDeep(this.accessories);
    const value = this.accessoriesFormGroup.controls[changedAccessory.id].value;
    if (Number(value) <= 0) {
      this.accessoriesFormGroup.controls[changedAccessory.id].setValue(0);
    } else if (isNumber(value) && !Number.isInteger(value)) {
      this.accessoriesFormGroup.controls[changedAccessory.id].setValue(Math.round(value));
    }
    accessories[index] = {
      ...accessories[index],
      quantity: this.accessoriesFormGroup.controls[changedAccessory.id].value,
    };
    this.accessories = accessories;
    this.store.dispatch(setAccessories({ accessories }));
  }

  getImagePath(finish: string, color: string): string {
    if (finish.toLowerCase() === 'magnelis') {
      color = 'magneziu';
    }
    finish = finish.replace(' ', '-').toLowerCase();
    return `https://expert-roof-systems.s3.eu-central-1.amazonaws.com/accessories/rain-system/${finish}-${color}.webp`;
  }

  hasProductsSelected(): boolean {
    const activeTab = this.calculationState.activeTab;
    const activeCalculation = this.calculationState[activeTab];
    if (activeCalculation.roofType !== CalculationTypeEnum.Custom) {
      return true;
    }
    if (this.accessories.length > 0) {
      return true;
    }
    const measurements = activeCalculation.measurements as CustomMeasurementDto;

    if (!!measurements?.tilesByHeight?.length && measurements.tilesByHeight[0].count) {
      return true;
    }
    return false;
  }

  async addToCart() {
    const calculation = await this.roofCalculationsService.calculateRoof(this.activeCalculation, this.models);
    const cart = await lastValueFrom(
      this.cartService.shoppingCartControllerAddToCart({
        body: {
          shoppingCartId: this.cart?.id,
          calculationId: calculation.id,
        },
      }),
    );
    this.store.dispatch(setCart({ cart }));
    this.sessionService.setCartId(cart.id);
  }

  setRainSystemCustomizations(rainSystemCustomizations: RainSystemCustomizationsDto) {
    this.rainSystemCustomizations = rainSystemCustomizations;
    const hasChangedDiameter =
      this.activeCalculation.rainSystem.diameter &&
      rainSystemCustomizations.diameter !== this.activeCalculation.rainSystem.diameter;
    this.updateRainSystemState();
    if (hasChangedDiameter) {
      this.calculateAccessories(this.activeCalculation, this.models, this.minimumAccessories ?? false);
    }
  }

  getAccessoryImage(id: string) {
    const imageUrl = this.allAccessories.find((element) => element.id === id)?.imageUrl;
    if (!imageUrl) {
      return null;
    }
    return this.imageBaseUrl + imageUrl;
  }
}
